import { ChangeDetectionStrategy, Component, inject, input, OnInit } from '@angular/core';

import { CommonModule } from '@angular/common';

import { ActivatedRoute, Router, RouterModule, UrlTree } from '@angular/router';
import { uniq } from 'lodash-es';
import { BehaviorSubject, combineLatest, Observable, of } from 'rxjs';
import { map, take, takeUntil, tap } from 'rxjs/operators';
import { ApiQuery, Restriction } from '../../../core/api/entities/api-query.entity';
import { BaseContainer } from '../../../core/common/containers/base.abstract.container';
import { FacetProfile } from '../../../core/profiles/entities/profile.entity';
import { ApiFragment } from '../../../core/records/entities/api-fragment.class';
import { ActionGroup } from '../../../elements/components/action-group/entities/action-group.entity';
import { ActionItem } from '../../../elements/components/action-item/entities/action-item.entity';
import { FormSelectOption } from '../../../form-controls/entities/form-select-option.entity';
import { RESULTS_PER_PAGE_OPTIONS } from '../../../overviews/generic-overview/components/display-settings-bar/entities/display-mode.entity';
import { SortOption } from '../../../overviews/generic-overview/components/display-settings-bar/entities/sort-option.entity';
import { PaginatedResults } from '../../../overviews/generic-overview/components/pagination/entities/paginated-results.entity';
import { ColumnDef } from '../../../overviews/list-view/entities/column-definition.entity';
import { SearchPageContainerComponent } from '../../../overviews/search-page/containers/search-page/search-page.container.component';
import { SearchPageConfig } from '../../../overviews/search-page/entities/search-page-config.entity';
import { SearchState } from '../../../overviews/search-page/entities/search-page-state.entity';
import { ShRecordTreePanelContainerComponent } from '../../../POCs/pim/containers/record-tree-panel/record-tree-panel-container.component';
import { ShProfileFacetListContainerComponent } from '../../../profile-facets/containers/profile-facet-list/profile-facet-list-container.component';
import { DottedKey } from '../../../public_api';
import { QueryIteratorStack } from '../../entities/query-iterator-stack.entity';
import { RecordSearchPageConfig } from '../../entities/record-search-page-config.entity';
import { RecordSearchPageSandbox } from '../../sandboxes/record-search-page.sandbox';

@Component({
    standalone: true,
    selector: 'sh-record-search-page',
    templateUrl: './record-search-page.container.component.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [
        CommonModule,
        RouterModule,
        SearchPageContainerComponent,
        ShProfileFacetListContainerComponent,
        ShRecordTreePanelContainerComponent
    ],
    providers: [RecordSearchPageSandbox]
})
export class RecordSearchPageContainerComponent extends BaseContainer implements OnInit {
    private readonly sandbox: RecordSearchPageSandbox = inject(RecordSearchPageSandbox);
    private readonly router: Router = inject(Router);
    private readonly activatedRoute: ActivatedRoute = inject(ActivatedRoute);

    // #region Inputs
    public readonly $config = input.required<RecordSearchPageConfig>({ alias: 'config' });
    // #endregion

    // #region Search config
    public config$: Observable<SearchPageConfig<ApiFragment>>;

    private readonly performRefresh$ = new BehaviorSubject<boolean>(true);
    // #endregion

    // #region Query variables
    public queryRestrictions: Restriction;
    private currentPaginatedResults: PaginatedResults<ApiFragment>;
    // #endregion

    // #region Facet variables
    public facetProfile$: Observable<FacetProfile>;
    // #endregion

    // #region Column variables
    public columns$: Observable<ColumnDef<any, ApiFragment>[]>;
    public columnOptions$: Observable<FormSelectOption[]>;
    // #endregion

    // #region FieldSelect variables
    private fieldSelect: DottedKey[];
    // #endregion

    // #region Sort variables
    public sortOptions$: Observable<SortOption[]>;
    // #endregion

    // #region Features
    public readonly $pimFeatureEnabled = this.sandbox.$pimPluginEnabled;
    // #endregion

    // #region QueryIterator variables
    private parentQueryIteratorStack: QueryIteratorStack;
    private queryIteratorStack: QueryIteratorStack = [];
    private readonly contextPageRecordTitle: string;
    private queryIteratorId: string;
    // #endregion

    ngOnInit(): void {
        this.initRestrictionListener();
        this.initFacetStreams();
        this.initColumnStreams();
        this.initFieldSelectListener();
        this.initParentQueryIteratorStack();
        this.initSearchPageConfig();
    }

    // #region SearchPageConfig

    private initSearchPageConfig(): void {
        this.config$ = combineLatest([this.getSortOptions()]).pipe(
            map(([sortOptions]) => this.generateConfig(sortOptions))
        );
    }

    private generateConfig(sortOptions: SortOption[]): SearchPageConfig<ApiFragment> {
        const config = this.$config();

        return {
            id: config.id,
            title: config.title,
            features: { ...config.features, hierarchy: this.isHierarchyEnabled(config) },
            sort: {
                default: config.sort.default,
                options: sortOptions
            },
            pageSize: {
                default: 25,
                options: RESULTS_PER_PAGE_OPTIONS
            },
            search: {
                searchBarMode: config.search.searchBarMode,
                refreshTriggers: [...config.search.refreshTriggers, this.performRefresh$.asObservable()],
                getSearchBarActionsFct: this.getSearchBarActions.bind(this),
                getSearchPageResultsFct: this.getSearchPageResults.bind(this),
                getSelectedItemsFct: this.getSelectedItems.bind(this)
            },
            list: {
                columns$: this.columns$,
                columnOptions$: this.columnOptions$
            },
            actions: {
                getGlobalActionsFct: this.getGlobalActions.bind(this),
                getActionsFct: this.getActions.bind(this),
                getBulkActionsFct: this.getBulkActions.bind(this),
                getBatchActionsFct: this.getBatchActions.bind(this),
                onRowClickFct: this.onRowClickFct.bind(this),
                onThumbnailClickedFct: this.onThumbnailClick.bind(this)
            },
            translations: {
                ...config.translations
            }
        };
    }
    // #endregion

    // #region Search
    private initRestrictionListener(): void {
        this.$config()
            .search.queryRestriction$.pipe(takeUntil(this.destroy$))
            .subscribe(restrictions => (this.queryRestrictions = restrictions));
    }

    private getSearchPageResults(state: SearchState): Observable<PaginatedResults<ApiFragment>> {
        this.createQueryContext(state, this.fieldSelect);
        return this.sandbox
            .getQueryContextPage(this.queryIteratorId, state.page)
            .pipe(tap(paginatedResults => (this.currentPaginatedResults = paginatedResults)));
    }

    private getSelectedItems(selectedItemIds: string[], state: SearchState): Observable<ApiFragment[]> {
        return this.sandbox.getAllRecordsWithId(selectedItemIds, state, this.queryRestrictions, this.fieldSelect);
    }

    private getSearchBarActions(): Observable<ActionItem[]> {
        const searchBarActionFct = this.$config().search.getSearchBarActionsFct;

        return searchBarActionFct ? searchBarActionFct() : of([]);
    }
    // #endregion

    // #region QueryIteratorStack
    private initParentQueryIteratorStack(): void {
        this.parentQueryIteratorStack =
            this.activatedRoute.parent?.snapshot.data?.routerState?.queryIteratorStack ?? [];
    }

    private createQueryContext(searchState: SearchState, fieldSelect: DottedKey[]): void {
        const query = new ApiQuery(searchState.structuredQuery, this.queryRestrictions, fieldSelect);

        const iterator = this.sandbox.createQueryContext({
            resultsPerPage: searchState.pageSize,
            sortInfo: searchState.sortInfo,
            query
        });

        this.queryIteratorId = iterator.id;

        this.queryIteratorStack = [
            ...this.parentQueryIteratorStack,
            {
                backToLangkey:
                    this.$config().translations?.backToLabelKey ??
                    this.sandbox.translate('components.pagination.back-to.search'),
                pageUrl: this.baseUrl,
                queryIteratorId: this.queryIteratorId
            }
        ];
    }
    // #endregion

    // #region Facets
    private initFacetStreams(): void {
        this.facetProfile$ = this.sandbox.getFacetProfile(this.$config().profileTags.facets);
    }
    // #endregion

    // #region Columns
    private initColumnStreams(): void {
        const predefinedColumns = this.$config().columns;

        this.columns$ = predefinedColumns
            ? of(predefinedColumns)
            : this.sandbox.getColumns(this.$config().id, this.$config().profileTags.list);

        this.columnOptions$ = predefinedColumns
            ? of([])
            : this.sandbox.getColumnOptions(this.$config().profileTags.list);
    }

    public onColumnSelectionChanged(selectedColumns: string[]): void {
        this.sandbox.setColumns(this.$config().id, selectedColumns);
    }
    // #endregion

    // #region FieldSelect
    private initFieldSelectListener(): void {
        const predefinedColumns = this.$config().columns;

        if (predefinedColumns) {
            this.fieldSelect = uniq(
                predefinedColumns
                    .flatMap(columns => columns.fieldSelectDottedKeys)
                    .concat(this.$config().search.fieldSelect)
            );

            return;
        }

        this.sandbox
            .getAllProfileColumns(this.$config().profileTags.list)
            .pipe(
                map(columns =>
                    uniq(
                        columns
                            .flatMap(column => column.fieldSelectDottedKeys ?? [])
                            .concat(this.$config().search.fieldSelect)
                    )
                ),
                take(1)
            )
            .subscribe(fieldSelect => (this.fieldSelect = fieldSelect));
    }
    // #endregion

    // #region Sort
    private getSortOptions(): Observable<SortOption[]> {
        return this.sandbox.getSortOptions(this.$config().profileTags.list, this.$config().sort.fixedOptions);
    }
    // #endregion

    // #region Features
    private isHierarchyEnabled(config: RecordSearchPageConfig): boolean {
        return this.$pimFeatureEnabled() && config.features.hierarchy;
    }
    // #endregion

    // #region Actions
    private getGlobalActions(): Observable<ActionItem[]> {
        return this.$config().actions.getGlobalActionsFct();
    }

    private getActions(records: ApiFragment[]): Observable<ActionGroup[]> {
        return this.$config().actions.getActionsFct(records, this.queryIteratorStack);
    }

    private getBulkActions(records: ApiFragment[]): Observable<ActionItem[]> {
        return this.$config().actions.getBulkActionsFct(records, this.queryIteratorStack);
    }

    private getBatchActions(): Observable<ActionItem[]> {
        const batchActionFct = this.$config().actions.getBatchActionsFct;

        return batchActionFct ? batchActionFct(this.currentPaginatedResults, this.queryIteratorStack) : of([]);
    }

    private onRowClickFct(item: ApiFragment): void {
        const onRowClickFct = this.$config().actions.onRowClickFct;

        if (onRowClickFct) {
            onRowClickFct(item, this.queryIteratorStack);
        }
    }
    // #endregion

    // #region Thumbnail
    public onThumbnailClick(record: ApiFragment): void {
        if (record.isNewCollection()) {
            this.navigateToDetail(record);
        } else {
            this.openPreview(record);
        }
    }

    private navigateToDetail(record: ApiFragment): void {
        this.router.navigate([record.getContentUrl()], { state: { queryIteratorStack: this.queryIteratorStack } });
    }

    private openPreview(record: ApiFragment): void {
        this.sandbox.setActive(this.queryIteratorId, record);

        const queryParams = this.sandbox.getQueryParams();

        this.router.navigate([{ outlets: { preview: ['preview'] } }], {
            relativeTo: this.activatedRoute,
            state: {
                queryIteratorStack: this.queryIteratorStack,
                queryParams,
                paginatedResults: this.currentPaginatedResults,
                contextPageRecordTitle: this.contextPageRecordTitle
            }
        });
    }

    // #endregion

    // #region Utils
    protected get baseUrl(): string {
        const urlTree: UrlTree = this.router.parseUrl(this.router.url);
        urlTree.queryParams = {};
        return urlTree.toString();
    }
    // #endregion
}
